import { BookOpen, Brain, Clock, Folder, Star, Tag } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { LibraryFolder } from "@/services/foldersService";
import type { MastermindSpace, Topic } from "@shared/schema";
import { buildFolderPath } from "@/components/library/buildFolderPath";

export type LibraryBreadcrumbSegment = {
  id: string;
  label: string;
  kind: "root" | "folder" | "spaces" | "space" | "topic" | "favorites" | "recent";
  onClick?: () => void;
};

type LibraryBreadcrumbsProps = {
  selectedFolderId: string | null;
  folders: LibraryFolder[];
  spaces: MastermindSpace[];
  topics: Topic[];
  filterSpaceId: string | null;
  filterTopicId: string | null;
  filterLibraryView?: "favorites" | "recent" | null;
  onNavigateRoot: () => void;
  onClearLibraryView?: () => void;
  onNavigateFolder: (folderId: string) => void;
  onNavigateSpaces?: () => void;
  onNavigateSpace: (spaceId: string) => void;
  onClearTopicFilter?: () => void;
  onClearSpaceFilter?: () => void;
};

export function buildLibraryBreadcrumbSegments(
  props: LibraryBreadcrumbsProps
): LibraryBreadcrumbSegment[] {
  const segments: LibraryBreadcrumbSegment[] = [
    { id: "root", label: "Library", kind: "root", onClick: props.onNavigateRoot },
  ];

  const folderPath = buildFolderPath(props.selectedFolderId, props.folders);
  for (const folder of folderPath) {
    segments.push({
      id: folder.id,
      label: folder.name,
      kind: "folder",
      onClick: () => props.onNavigateFolder(folder.id),
    });
  }

  if (props.selectedFolderId === "spaces") {
    segments.push({
      id: "spaces",
      label: "Spaces",
      kind: "spaces",
    });
  } else if (props.selectedFolderId?.startsWith("space-")) {
    const spaceId = props.selectedFolderId.replace("space-", "");
    const space = props.spaces.find((s) => s.id === spaceId);
    segments.push({
      id: props.selectedFolderId,
      label: space?.name ?? "Space",
      kind: "space",
      onClick: () => props.onNavigateSpace(spaceId),
    });
  }

  if (props.filterSpaceId && !props.selectedFolderId?.startsWith("space-")) {
    const space = props.spaces.find((s) => s.id === props.filterSpaceId);
    segments.push({
      id: `filter-space-${props.filterSpaceId}`,
      label: space?.name ?? "Space",
      kind: "space",
      onClick: props.onClearSpaceFilter,
    });
  }

  if (props.filterTopicId) {
    const topic = props.topics.find((t) => t.id === props.filterTopicId);
    segments.push({
      id: `filter-topic-${props.filterTopicId}`,
      label: topic?.name ?? "Topic",
      kind: "topic",
      onClick: props.onClearTopicFilter,
    });
  }

  return segments;
}

function segmentIcon(kind: LibraryBreadcrumbSegment["kind"]) {
  switch (kind) {
    case "root":
      return <BookOpen className="h-3.5 w-3.5 shrink-0" />;
    case "folder":
      return <Folder className="h-3.5 w-3.5 shrink-0 text-amber-600" />;
    case "spaces":
    case "space":
      return <Brain className="h-3.5 w-3.5 shrink-0 text-purple-600" />;
    case "topic":
      return <Tag className="h-3.5 w-3.5 shrink-0" />;
    case "favorites":
      return <Star className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
    case "recent":
      return <Clock className="h-3.5 w-3.5 shrink-0" />;
    default:
      return null;
  }
}

export function LibraryBreadcrumbs(props: LibraryBreadcrumbsProps) {
  const segments = buildLibraryBreadcrumbSegments(props);
  const lastIndex = segments.length - 1;

  // DS EDIT 5aug26 — at the Library root the lone "Library" crumb duplicated the
  // page heading right above it; render nothing until there's an actual path.
  if (segments.length <= 1) return null;

  return (
    <Breadcrumb className="mb-4">
      <BreadcrumbList>
        {segments.map((segment, index) => (
          <span key={segment.id} className="inline-flex items-center gap-1.5">
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              {index === lastIndex ? (
                <BreadcrumbPage className="flex items-center gap-1.5 max-w-[200px] sm:max-w-none">
                  {segmentIcon(segment.kind)}
                  <span className="truncate">{segment.label}</span>
                </BreadcrumbPage>
              ) : segment.onClick ? (
                <BreadcrumbLink asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 max-w-[160px] sm:max-w-none truncate"
                    onClick={segment.onClick}
                  >
                    {segmentIcon(segment.kind)}
                    <span className="truncate">{segment.label}</span>
                  </button>
                </BreadcrumbLink>
              ) : (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  {segmentIcon(segment.kind)}
                  <span className="truncate">{segment.label}</span>
                </span>
              )}
            </BreadcrumbItem>
          </span>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
